import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, TextField, Typography } from "@mui/material";
import { forwardRef, useImperativeHandle, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { SubmissionPayload } from "../../../types/Inspection";

const schema = yup.object({
  comment: yup.string().optional().default(""),
});

interface FormData {
  comment: string;
  classification: "A" | "A2" | "B" | "C" | "Q";
}

export interface QClassificationPayload {
  trackingnumber: number;
  productionOrderNumber: string;
  articleNumber?: string;
  classification: "A" | "A2" | "B" | "C" | "Q";
  comment?: string;
  visualInspection?: SubmissionPayload;
  elInspection?: SubmissionPayload;
}

interface Articles {
  A: string | undefined;
  A2: string | undefined;
  B: string | undefined;
  C: string | undefined;
  Q: string | undefined;
}

export type ClassificationMessageRef = {
  reset: () => void;
};

type Props = {
  onSubmit: (data: QClassificationPayload) => void;
  isValidModule: boolean;
  orderNumber?: string;
  trackingNumber?: number;
  articles?: Articles;
  visualInspection?: SubmissionPayload;
  elInspection?: SubmissionPayload;
};

const ClassificationForm = forwardRef<ClassificationMessageRef, Props>(
  (
    {
      onSubmit,
      isValidModule,
      orderNumber,
      trackingNumber,
      articles,
      visualInspection,
      elInspection,
    },
    ref
  ) => {
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const {
      control,
      reset,
      getValues,
      formState: { errors },
    } = useForm<Omit<FormData, "classification">>({
      resolver: yupResolver(schema),
      defaultValues: {
        comment: "",
      },
    });

    const handleClassify = (
      classification: QClassificationPayload["classification"]
    ) => {
      if (!trackingNumber) return;
      if (!orderNumber) return;
      if (!articles) return;

      const articleNumber = articles[classification];

      if (!articleNumber && classification !== "Q") {
        setErrorMessage(`No Article found for Palette ${classification}`);
        return;
      }

      const validModuleClassification = ["A", "A2", "B"].includes(
        classification
      );
      const invalidModuleClassification = ["C", "Q"].includes(classification);
      const defectRequired = ["B", "C", "Q"].includes(classification);
      const values = getValues();
      const visual = visualInspection?.classification;
      const el = elInspection?.classification;

      const blockedVisual = ["B", "C", "Q"].includes(visual || "");
      const blockedEL = ["B", "C", "Q"].includes(el || "");

      if (validModuleClassification && !isValidModule) {
        setErrorMessage(
          `Classification ${classification} is not allowed because the tolerance is invalid.`
        );
        return;
      }

      if (invalidModuleClassification && isValidModule) {
        setErrorMessage(
          `Classification ${classification} is not allowed because the tolerance valid.`
        );
        return;
      }

      if (defectRequired) {
        const visualReadable = visual || "not set";
        const elReadable = el || "not set";
        if (!blockedVisual && !blockedEL) {
          setErrorMessage(
            `Classification of ${classification} module requires at least one defect. (Visual Inspection = ${visualReadable}, EL Inspection = ${elReadable})`
          );
          return;
        }
      }

      if (!defectRequired) {
        if (blockedVisual || blockedEL) {
          const both = [
            blockedVisual ? `Visual Inspection = ${visual}` : null,
            blockedEL ? `EL Inspection = ${el}` : null,
          ]
            .filter(Boolean)
            .join(" and ");
          setErrorMessage(
            `Classification of ${classification} module must not have any "${
              blockedVisual ? visual : el
            }" inspection. (${both})`
          );
          return;
        }
      }

      if (
        (visual === "B" || el === "B") &&
        ["A", "A2"].includes(classification)
      ) {
        setErrorMessage(
          `Classification ${classification} is not allowed because one inspection is B.`
        );
        return;
      }

      if (
        (visual === "C" || el === "C") &&
        ["A", "A2"].includes(classification)
      ) {
        setErrorMessage(
          `Classification ${classification} is not allowed because one inspection is C.`
        );
        return;
      }

      if (
        (visual === "Q" || el === "Q") &&
        ["A", "A2"].includes(classification)
      ) {
        setErrorMessage(
          `Classification ${classification} is not allowed because one inspection is Q.`
        );
        return;
      }

      const payload: QClassificationPayload = {
        trackingnumber: trackingNumber,
        productionOrderNumber: orderNumber,
        articleNumber,
        classification,
        comment: values.comment || "",
        visualInspection,
        elInspection,
      };

      try {
        onSubmit(payload);
        setSuccessMessage(
          `Module successfully classified as ${classification}`
        );
        clear({ all: false });
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        }
      }
    };

    const clear = ({ all }: { all: boolean }) => {
      reset();
      setErrorMessage(null);

      if (all) {
        setSuccessMessage(null);
      }
    };

    useImperativeHandle(ref, () => ({
      reset: () => clear({ all: true }),
    }));

    return (
      <Box>
        <Typography variant="h4" sx={{ mt: 2, mb: 2 }}>
          Classification
        </Typography>

        <Box mb={2}>
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                rows={3}
                label="Comment"
                error={!!errors.comment}
                helperText={errors.comment?.message}
              />
            )}
          />
        </Box>

        <Box display="flex" gap={2}>
          {(["A", "A2", "B", "C", "Q"] as const).map((classification) => (
            <Button
              key={classification}
              variant="outlined"
              fullWidth
              sx={{
                fontSize: 23,
                borderColor:
                  classification === "A"
                    ? "green"
                    : classification === "A2"
                    ? "blue"
                    : classification === "B"
                    ? "orange"
                    : classification === "C"
                    ? "red"
                    : "purple",
                color:
                  classification === "A"
                    ? "green"
                    : classification === "A2"
                    ? "blue"
                    : classification === "B"
                    ? "orange"
                    : classification === "C"
                    ? "red"
                    : "purple",
                minHeight: 125,
              }}
              onClick={() => handleClassify(classification)}
            >
              {classification}
            </Button>
          ))}
        </Box>

        {successMessage && (
          <Typography
            role="status"
            color="success.main"
            sx={{ mt: 2, fontWeight: "bold", fontSize: "1.25rem" }}
          >
            {successMessage}
          </Typography>
        )}

        {errorMessage && (
          <Typography
            role="alert"
            color="error.main"
            sx={{ mt: 2, fontWeight: "bold", fontSize: "1.25rem" }}
          >
            {errorMessage}
          </Typography>
        )}
      </Box>
    );
  }
);

export default ClassificationForm;
